import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Select,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  Pill,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconSearch,
  IconPlus,
  IconTrash,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useGroupPermissions,
  useCreateGroupPermission,
  useDeleteGroupPermission,
} from "../../../hooks/admin/useGroupPermissions";
import { useGroupsAdmin } from "../../../hooks/admin/useGroups";
import { usePermissionsAdmin } from "../../../hooks/admin/usePermissions";
import { GroupPermission } from "../../../services/admin/grouppermissions.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";
import { formatPermissionLabel } from "../../../utils/formatters";

const ITEMS_PER_PAGE = 20;

interface FormValues {
  groupId: string;
  permissionId: string;
}

interface GPFilters {
  groupId?: string | null;
  permissionId?: string | null;
}

export default function GroupPermissionsAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [gpToDelete, setGpToDelete] = useState<GroupPermission | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<GPFilters>({});
  const [tempFilters, setTempFilters] = useState<GPFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const {
    data: groupPermissions = [],
    isLoading,
    refetch,
  } = useGroupPermissions();
  const { data: groups = [] } = useGroupsAdmin();
  const { data: permissions = [] } = usePermissionsAdmin();
  const createGroupPermission = useCreateGroupPermission();
  const deleteGroupPermission = useDeleteGroupPermission();

  // Form
  const form = useForm<FormValues>({
    initialValues: {
      groupId: "",
      permissionId: "",
    },
    validate: {
      groupId: required("Group is required"),
      permissionId: required("Permission is required"),
    },
  });

  // Filter by search and filters
  const filteredGroupPermissions = useMemo(() => {
    let result = groupPermissions;

    // Filter by group
    if (filters.groupId) {
      result = result.filter((gp) => gp.groupId === filters.groupId);
    }

    // Filter by permission
    if (filters.permissionId) {
      result = result.filter((gp) => gp.permissionId === filters.permissionId);
    }

    // Filter by search text
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (gp) =>
          gp.group?.name.toLowerCase().includes(searchLower) ||
          gp.permission?.name.toLowerCase().includes(searchLower) ||
          gp.permission?.description?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [groupPermissions, debouncedSearch, filters]);

  // Check if any filters are active
  const hasActiveFilters = !!filters.groupId || !!filters.permissionId;

  // Pagination
  const totalPages = Math.ceil(
    filteredGroupPermissions.length / ITEMS_PER_PAGE
  );
  const paginatedGPs = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredGroupPermissions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredGroupPermissions, page]);

  // Options for form dropdowns (only active groups)
  const groupOptions = groups
    .filter((g) => g.isActive)
    .map((g) => ({ value: g.id, label: g.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const permissionOptions = permissions
    .map((p) => ({
      value: p.id,
      label: formatPermissionLabel(p),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Options for filter dropdowns (all groups, shorter labels)
  const filterGroupOptions = groups
    .map((g) => ({ value: g.id, label: g.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const filterPermissionOptions = permissions
    .map((p) => ({ value: p.id, label: formatPermissionLabel(p) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Handlers
  const handleAdd = () => {
    form.reset();
    openFormModal();
  };

  const handleSubmit = form.onSubmit(
    async (values) => {
      await createGroupPermission.mutateAsync(values);
      closeFormModal();
      form.reset();
    },
    () => scrollToFirstError(form)
  );

  const handleDeleteClick = (gp: GroupPermission) => {
    setGpToDelete(gp);
    openConfirmModal();
  };

  const confirmDelete = async () => {
    if (gpToDelete) {
      await deleteGroupPermission.mutateAsync(gpToDelete.id);
      closeConfirmModal();
      setGpToDelete(null);
    }
  };

  const handleOpenFilterModal = () => {
    setTempFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleRemoveFilter = (key: keyof GPFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setPage(1);
  };

  // Get display names for active filters
  const getFilterDisplayName = (key: keyof GPFilters): string | null => {
    if (key === "groupId" && filters.groupId) {
      return groups.find((g) => g.id === filters.groupId)?.name || null;
    }
    if (key === "permissionId" && filters.permissionId) {
      const perm = permissions.find((p) => p.id === filters.permissionId);
      return perm ? formatPermissionLabel(perm) : null;
    }
    return null;
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Group Permissions</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage which permissions are assigned to each group
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by group or permission name..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" align="center">
                    <Text size="sm" c="dimmed">
                      Filters:
                    </Text>
                    {filters.groupId && (
                      <Pill
                        withRemoveButton
                        onRemove={() => handleRemoveFilter("groupId")}
                      >
                        {getFilterDisplayName("groupId")}
                      </Pill>
                    )}
                    {filters.permissionId && (
                      <Pill
                        withRemoveButton
                        onRemove={() => handleRemoveFilter("permissionId")}
                      >
                        {getFilterDisplayName("permissionId")}
                      </Pill>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  Showing {filteredGroupPermissions.length} of{" "}
                  {groupPermissions.length} group permissions
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Group Permissions ({filteredGroupPermissions.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredGroupPermissions.length
                      )}{" "}
                      of {filteredGroupPermissions.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredGroupPermissions.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search || hasActiveFilters
                    ? "No group permissions match the current filters"
                    : "No group permissions found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Group</Table.Th>
                      <Table.Th>Permission</Table.Th>
                      <Table.Th>Added</Table.Th>
                      <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedGPs.map((gp) => (
                      <Table.Tr key={gp.id}>
                        {/* Group */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {gp.group?.name || "—"}
                          </Text>
                        </Table.Td>

                        {/* Permission */}
                        <Table.Td>
                          <div>
                            <Text size="sm" fw={500}>
                              {formatPermissionLabel(gp.permission)}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {gp.permission?.name || ""}
                            </Text>
                          </div>
                        </Table.Td>

                        {/* Added */}
                        <Table.Td>
                          <Text size="sm">
                            {new Date(gp.createdAt).toLocaleDateString()}
                          </Text>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Tooltip label="Remove">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => handleDeleteClick(gp)}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add Permission to Group Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title="Add Permission to Group"
          size="md"
          closeOnClickOutside={false}
        >
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <Select
                label="Group"
                placeholder="Select a group"
                data={groupOptions}
                searchable
                withAsterisk
                {...form.getInputProps("groupId")}
              />

              <Select
                label="Permission"
                placeholder="Select a permission"
                data={permissionOptions}
                searchable
                withAsterisk
                {...form.getInputProps("permissionId")}
              />

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={closeFormModal}>
                  Cancel
                </Button>
                <Button type="submit" loading={createGroupPermission.isPending}>
                  Add Permission
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Group Permissions"
          size="sm"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Group"
              placeholder="All groups"
              data={filterGroupOptions}
              value={tempFilters.groupId || null}
              onChange={(value) =>
                setTempFilters((prev) => ({ ...prev, groupId: value }))
              }
              clearable
              searchable
            />

            <Select
              label="Permission"
              placeholder="All permissions"
              data={filterPermissionOptions}
              value={tempFilters.permissionId || null}
              onChange={(value) =>
                setTempFilters((prev) => ({ ...prev, permissionId: value }))
              }
              clearable
              searchable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Delete */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title="Remove Permission"
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to remove the permission{" "}
              <strong>{formatPermissionLabel(gpToDelete?.permission)}</strong>{" "}
              from group <strong>{gpToDelete?.group?.name}</strong>?
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteGroupPermission.isPending}
              >
                Remove
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
